import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useMobileFooterContainer } from '@/app/shell/NavLayout';
import { INCIDENT_PANEL_CSS_CLASS, type IncidentPanel } from './incidentPanel';
import { IncidentDetailMobilePanelSwitch } from './IncidentDetailMobilePanelSwitch';

export type IncidentDetailLayoutBoxProps = {
  chatArea: ReactNode;
  videoPanelArea: ReactNode;
  bannersArea?: ReactNode;
  actionBarArea: ReactNode;
  dataPanelArea: ReactNode;
  mapArea: ReactNode;
  hasVideoPanel: boolean;
  incidentIsClosed: boolean;
  initialActivePanel?: IncidentPanel | null;
  hasActiveAppleEedVideo?: boolean;
  videoExpanded?: boolean;
};

export function IncidentDetailLayoutBox({
  chatArea,
  videoPanelArea,
  bannersArea,
  actionBarArea,
  dataPanelArea,
  mapArea,
  hasVideoPanel,
  incidentIsClosed,
  initialActivePanel = null,
  hasActiveAppleEedVideo = false,
  videoExpanded = false,
}: IncidentDetailLayoutBoxProps) {
  const [activePanel, setActivePanel] = useState<IncidentPanel>(initialActivePanel ?? 'videoPanel');
  const panelSelectionResolved = useRef(initialActivePanel !== null);
  const mobileFooterContainer = useMobileFooterContainer();

  useEffect(() => {
    if (panelSelectionResolved.current || initialActivePanel === null) {
      return;
    }

    panelSelectionResolved.current = true;
    setActivePanel(initialActivePanel);
  }, [initialActivePanel]);

  const selectPanel = (panel: IncidentPanel) => {
    panelSelectionResolved.current = true;
    setActivePanel(panel);
  };

  const activePanelClass = INCIDENT_PANEL_CSS_CLASS[activePanel];
  const appleEedVideoClass = hasActiveAppleEedVideo ? 'incident-detail-layout-box--apple-eed-video' : '';
  const videoExpandedClass = videoExpanded ? 'incident-detail-layout-box--video-expanded' : '';
  const noVideoPanelClass = hasVideoPanel ? '' : 'incident-detail-layout-box--no-video-panel';

  return (
    <>
      <div
        className={`incident-detail-layout-box ${activePanelClass} ${appleEedVideoClass} ${videoExpandedClass} ${noVideoPanelClass}`}
      >
        <div className="incident-detail-layout-box__data-panel">{dataPanelArea}</div>

        <div className="incident-detail-layout-box__banners">{bannersArea}</div>

        <div className="incident-detail-layout-box__action-bar">{actionBarArea}</div>

        <div className="incident-detail-layout-box__map">{mapArea}</div>

        <div className="incident-detail-layout-box__video-panel">{videoPanelArea}</div>

        <div className="incident-detail-layout-box__chat">{chatArea}</div>
      </div>

      {mobileFooterContainer !== null &&
        createPortal(
          <IncidentDetailMobilePanelSwitch
            activePanel={activePanel}
            onActivePanelChanged={selectPanel}
            incidentIsClosed={incidentIsClosed}
          />,
          mobileFooterContainer,
        )}
    </>
  );
}
